import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import ArticleCard from '@/components/base/ArticleCard';
import { articleMap, articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;
  const relatedArticles = useMemo(
    () => {
      if (!article) return [];
      return articles
        .filter((a) => a.id !== article.id && a.categoryId === article.categoryId)
        .slice(0, 3);
    },
    [article?.id, article?.categoryId]
  );

  if (!article) {
    return <NotFound />;
  }

  const category = categoryMap[article.categorySlug];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: 'Hitozuma 专题导览',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Hitozuma 专题导览',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - Hitozuma 专题导览`}
        description={article.excerpt}
        keywords={article.tags.map((t) => `hitozuma ${t}`).join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogImage={article.imageUrl}
        ogType="article"
        schema={articleSchema}
      />
      <Navbar />

      <main className="pt-16">
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category
                ? [{ label: category.name, href: `/category/${article.categorySlug}` }]
                : []),
              { label: article.title },
            ]}
          />
        </div>

        <article className="px-4 md:px-6 max-w-4xl mx-auto pb-14 md:pb-20">
          <header className="py-8 md:py-10">
            <div className="flex items-center gap-3 flex-wrap mb-4">
              {category && (
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-500">{article.publishDate}</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mt-4 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>

          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <span className="text-sm text-foreground-500">分享文章：</span>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href="#"
                  className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:bg-primary-100 transition-colors cursor-pointer"
                  aria-label="Share on Twitter"
                >
                  <i className="ri-twitter-x-line"></i>
                </a>
                <a
                  href="#"
                  className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:bg-primary-100 transition-colors cursor-pointer"
                  aria-label="Share on Facebook"
                >
                  <i className="ri-facebook-line"></i>
                </a>
                <a
                  href="#"
                  className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:bg-primary-100 transition-colors cursor-pointer"
                  aria-label="Copy link"
                >
                  <i className="ri-link"></i>
                </a>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-6">
                相关文章推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}